import React from 'react';
import EditFieldModal from '../Edit items/EditFieldModal';
import ShipmentList from './ShipmentList';
import { Card, CardHeader, CopyButton, Empty, Label } from './primitives';

/*
 * Where it goes and how it gets there.
 *
 * The embedded map, the raw latitude/longitude pair and the address photos were
 * dropped as unused in practice — the map in particular was a third-party
 * iframe worth several hundred kilobytes loading on every order view. The edit
 * components for the coordinates and photos still exist under ./Edit items if
 * that call is ever reversed.
 *
 * Addresses arrive as one comma-joined string ("7003,Aldimashqi,Ar Rahmaniyah")
 * with no separate city or district fields to lay out. Splitting on the commas
 * is what turns that into something readable at a glance rather than a run-on
 * line, which matters because this is the block someone reads aloud on the
 * phone to a courier.
 */

const addressLines = (address) =>
  typeof address === 'string'
    ? address
        .split(',')
        .map((line) => line.trim())
        .filter(Boolean)
    : [];

const DeliveryCard = ({
  order,
  orderId,
  isEditable,
  // The parcel's trips, each with its own tracking number (see ShipmentList).
  shipments,
  courierLabel,
  onAddShipment,
  onEditShipment,
  onRemoveShipment,
  onSaved,
}) => {
  const lines = addressLines(order.address);

  return (
    <Card>
      <CardHeader
        title="Delivery"
        action={
          isEditable && (
            <EditFieldModal
              fieldKey="address"
              fieldLabel="Address"
              fieldValue={order.address}
              id={orderId}
              modalId="editaddressModal"
              title="Edit Address"
              placeholder="Enter address"
              onSaved={onSaved}
            />
          )
        }
      />
      <div className="px-3.5 py-3 flex flex-col gap-3">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <Label>Address</Label>
            <CopyButton value={order.address} label="Copy address" />
          </div>
          {lines.length > 0 ? (
            <address className="not-italic text-[13px] font-semibold text-nysmah-dark leading-[1.5]">
              {lines.map((line, index) => (
                <span key={`${line}-${index}`} className="block">
                  {line}
                </span>
              ))}
            </address>
          ) : (
            <span className="text-[13px]">
              <Empty>No address on this order.</Empty>
            </span>
          )}
        </div>

        {order.shippingMethod && (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[12px] font-medium text-[#6B7C86] shrink-0">
              Shipping method
            </span>
            <span className="text-[13px] font-semibold text-nysmah-dark text-right">
              {order.shippingMethod}
            </span>
          </div>
        )}

        <div className="pt-3 border-t border-[#E1E7EA]">
          <Label className="block mb-2">Shipments</Label>
          <ShipmentList
            shipments={shipments}
            courierLabel={courierLabel}
            isEditable={isEditable}
            onAdd={onAddShipment}
            onEdit={onEditShipment}
            onRemove={onRemoveShipment}
          />
        </div>
      </div>
    </Card>
  );
};

export default DeliveryCard;
